import { useMemo } from 'react';
import zxcvbn from 'zxcvbn';

const LABELS = ['Very weak', 'Weak', 'Fair', 'Good', 'Strong'];
const COLORS = [
  'bg-red-500',
  'bg-red-400',
  'bg-amber-400',
  'bg-emerald-400',
  'bg-emerald-500',
];
const TEXT_COLORS = [
  'text-red-400',
  'text-red-400',
  'text-amber-400',
  'text-emerald-400',
  'text-emerald-400',
];

/**
 * Non-blocking password strength meter (zxcvbn).
 * Shows a 4-segment bar, label, and optional warning/suggestions.
 * Does NOT prevent form submission — purely educational.
 */
export default function PasswordStrengthMeter({ password, userInputs = [] }) {
  const result = useMemo(() => {
    if (!password) return null;
    try {
      return zxcvbn(password, userInputs);
    } catch {
      return null;
    }
  }, [password, userInputs]);

  if (!password) return null;

  const score = result?.score ?? 0;
  const warning = result?.feedback?.warning;
  const suggestions = result?.feedback?.suggestions || [];

  return (
    <div data-testid="password-strength-meter" className="space-y-1.5 pt-1">
      <div className="flex items-center gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors ${
              i < Math.max(1, score) ? COLORS[score] : 'bg-white/10'
            }`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-[11px]">
        <span data-testid="password-strength-label" className={`font-medium ${TEXT_COLORS[score]}`}>
          {LABELS[score]}
        </span>
        {warning && (
          <span className="text-muted-foreground/80 text-right max-w-[70%] truncate" title={warning}>
            {warning}
          </span>
        )}
      </div>
      {!warning && suggestions.length > 0 && score < 3 && (
        <p className="text-[10px] text-muted-foreground/70 leading-relaxed">
          Tip: {suggestions[0]}
        </p>
      )}
    </div>
  );
}
